import { useRef, useEffect } from 'react'
import styled from 'styled-components'
import { SQUARE_ROOT_CHAR, convertPreviousSqrt } from 'util/convertSqrt'
import { ElementTohtml, htmlToElement } from './htmlToElement'

export const RICH_CONTENT_SPECIAL_SYMBOL_CLASS_NAME =
  'rich-content-special-symbol'
export const RICH_CONTENT_FONT_FAMILY = "'Nunito', 'STIX Two Math', sans-serif"

const HtmlParserSpan = styled.span`
  font-family: ${RICH_CONTENT_FONT_FAMILY};

  .${RICH_CONTENT_SPECIAL_SYMBOL_CLASS_NAME} {
    font-family: ${RICH_CONTENT_FONT_FAMILY};
    font-size: inherit;
    line-height: inherit;
  }

  .math_frac {
    display: inline-block;
    position: relative;
    vertical-align: middle;
    letter-spacing: 0.001em;
    text-align: center;
    margin-right: 5px;
    font-size: 19px;
    color: #707070;
    margin-left: 5px;

    span {
      display: block;
      padding: 0.1em;

      &.bottom {
        border-top: thin solid currentColor;
      }

      &.symbol {
        display: none;
      }
    }
  }

  .spanNextToFraction {
    margin: 0;
    font-size: 19px;
    color: #707070;
  }
`

const normalizeTableCellBorderColor = (html: string) => {
  if (!html) {
    return html
  }

  const htmlElement = htmlToElement(html)

  /**
   * Jodit Wysiwyg is applying special styles to the table cells which are not then available in the html that is produced
   * hence we have to replicate those styles here to make sure that the table cells are rendered properly.
   */
  htmlElement.querySelectorAll('td').forEach((tableCell) => {
    tableCell.style.padding = tableCell.style.padding || '.4em'
    tableCell.style.fontSize = tableCell.style.fontSize || '19px'
    tableCell.style.fontFamily =
      tableCell.style.fontFamily || RICH_CONTENT_FONT_FAMILY

    const borderWidth = tableCell.style.borderWidth || '1px'
    const borderStyle = tableCell.style.borderStyle || 'solid'
    const borderColor = tableCell.style.borderColor || '#dadada'

    tableCell.style.border = `${borderWidth} ${borderStyle} ${borderColor}`
  })

  return ElementTohtml(htmlElement)
}

const normalizeOverlineAccessibility = (html: string) => {
  if (!html) {
    return html
  }

  const htmlElement = htmlToElement(html)

  /**
   * Rich editor overline markup is used to represent repeating decimals visually,
   * but screen readers do not announce that decoration. We keep the visible
   * overline span for sighted users, hide that fragment from assistive tech, and
   * inject an sr-only phrase so values like `0.<span overline>10</span>` are read
   * in sequence as "zero point 10 repeating decimal".
   */
  htmlElement.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
    const textDecoration =
      element.style.textDecoration || element.style.textDecorationLine
    const overlineText = element.textContent?.trim()
    const isNumeric = !!overlineText && !isNaN(Number(overlineText))
    const isOverline = textDecoration.includes('overline')

    /**
     * Only read of the overlined text is also numeric
     */
    if (overlineText && isOverline && isNumeric) {
      element.setAttribute('aria-hidden', 'true')

      const srOnlyElement = document.createElement('span')
      srOnlyElement.className = 'sr-only'
      srOnlyElement.textContent = `${overlineText} repeating decimal`

      element.insertAdjacentElement('afterend', srOnlyElement)
    }
  })

  return ElementTohtml(htmlElement)
}

const normalizeImageSizing = (html: string) => {
  if (!html) {
    return html
  }

  const htmlElement = htmlToElement(html)

  /**
   * Authored assessment and assignment content can include oversized inline images.
   * Normalizing image sizing here keeps those images inside the viewer width while
   * preserving their aspect ratio across teacher mode, student mode, and preview flows.
   */
  htmlElement.querySelectorAll('img').forEach((imageElement) => {
    imageElement.style.maxWidth = '100%'
    imageElement.style.height = 'auto'
  })

  return ElementTohtml(htmlElement)
}

export default function HtmlParser(props: {
  html: string
  id?: string
  className?: string
  onClick?: () => void
}) {
  const spanRef = useRef<HTMLSpanElement>(null)
  const { html, id, className, onClick } = props

  useEffect(() => {
    if (spanRef.current) {
      let updatedHtml = html
      const isSqrtExists = updatedHtml?.includes(SQUARE_ROOT_CHAR)
      if (isSqrtExists) {
        const htmlElement = htmlToElement(html)
        convertPreviousSqrt(htmlElement)
        updatedHtml = ElementTohtml(htmlElement)
      }

      /**
       * Each transformation step is kept separate so its easy to debug html after each stage
       * and fix any bugs independently without affecting the other transformations.
       */
      updatedHtml = normalizeTableCellBorderColor(updatedHtml)
      updatedHtml = normalizeImageSizing(updatedHtml)
      updatedHtml = normalizeOverlineAccessibility(updatedHtml)

      spanRef.current.innerHTML = updatedHtml
    }
  }, [spanRef, html])

  return (
    <HtmlParserSpan
      id={id}
      className={className}
      onClick={onClick}
      ref={spanRef}
    />
  )
}
